import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { RecipientInput } from "@/components/quotation/RecipientInput";

const L = ({ children }) => <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</Label>;

async function openPdf(q) {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (w) w.location.href = url; else window.open(url, "_blank");
  } catch (e) { w?.close(); toast.error("Could not open the PDF"); }
}

export function SendEmailDialog({ quotation, open, onOpenChange, onSent }) {
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({ to: [], cc: [], subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) { return undefined; }
    let live = true;
    setDraft(null); setError("");
    api.get(`/quotations/${quotation.id}/email-draft`)
      .then((r) => { if (live) { setDraft(r.data); setForm({ to: r.data.to, cc: r.data.cc, subject: r.data.subject, body: r.data.body }); } })
      .catch((e) => { if (live) setError(formatApiError(e)); });
    return () => { live = false; };
  }, [open, quotation.id]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    setSending(true); setError("");
    try {
      const r = await api.post(`/quotations/${quotation.id}/send-email`, form);
      toast.success(`${r.data.display_number} emailed to ${form.to.join(", ")}`);
      onSent?.(r.data);
    } catch (e) { const m = formatApiError(e); setError(m); toast.error(m); } finally { setSending(false); }
  };
  const ready = draft && form.to.length && form.subject.trim() && form.body.trim();

  return (
    <Dialog open={open} onOpenChange={(v) => !sending && onOpenChange(v)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>Send {quotation.display_number} by email</DialogTitle>
          <DialogDescription>Review and edit before sending. Changes apply to this email only.</DialogDescription>
        </DialogHeader>
        {!draft && !error && <div className="py-10 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />Preparing email…</div>}
        {draft && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500" data-testid="send-email-from">From: <b className="text-slate-700">{draft.from_name} &lt;{draft.from}&gt;</b></div>
            <div><L>To</L><div className="mt-1"><RecipientInput value={form.to} onChange={set("to")} testId="send-email-to" placeholder="client@company.com" /></div></div>
            <div><L>CC</L><div className="mt-1"><RecipientInput value={form.cc} onChange={set("cc")} testId="send-email-cc" placeholder="Add CC" /></div></div>
            <div><L>Subject</L><Input value={form.subject} onChange={(e) => set("subject")(e.target.value)} className="mt-1" data-testid="send-email-subject" /></div>
            <div><L>Message</L>
              <textarea value={form.body} onChange={(e) => set("body")(e.target.value)} rows={14} className="mt-1 w-full border border-slate-200 rounded-md p-3 text-sm leading-relaxed font-sans" data-testid="send-email-body" />
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Added below your message</div>
              <div className="bg-white rounded border border-slate-100 p-3 overflow-x-auto" data-testid="send-email-signature" dangerouslySetInnerHTML={{ __html: draft.signature_html }} />
              {draft.footer && <div className="text-[11px] text-slate-400 mt-2" data-testid="send-email-footer">{draft.footer}</div>}
            </div>
            <div className="flex items-center gap-2 text-sm" data-testid="send-email-attachment">
              <FileText className="w-4 h-4 text-rose-600" /><span className="font-mono text-xs text-slate-700 truncate">{draft.pdf_filename}</span>
              <button type="button" onClick={() => openPdf(quotation)} className="text-indigo-600 hover:underline text-xs" data-testid="send-email-preview-pdf">Preview PDF</button>
            </div>
          </div>
        )}
        {error && <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm p-3 flex gap-2" data-testid="send-email-error"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="send-email-cancel">Cancel</Button>
          <Button onClick={send} disabled={!ready || sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-email-confirm">{sending ? "Sending…" : "Send"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
